import type { OrderInfo } from '@@partner:types:app'

import React, { useMemo, useCallback } from 'react'
import { useHistory, generatePath, Link } from 'react-router-dom'
import { PersonOutline, CarOutline } from 'react-ionicons'

import { getUrgencyColor } from './kanbanHelpers'
import { isEditable } from '@@partner:helpers/order'
import {
  ROUTE_ORDERS_EDIT_ORDER,
  ROUTE_ORDERS_VIEW_ORDER
} from '@@partner:config/routes'

import './OrderCard.scss'

export interface OrderCardProps {
  order: OrderInfo
  brandSlug: string
}

function OrderCard ({ order, brandSlug }: OrderCardProps) {
  const history = useHistory()

  // URL для редактирования/просмотра заказа
  const orderRoute = useMemo(() => {
    return isEditable(order.status) ? ROUTE_ORDERS_EDIT_ORDER : ROUTE_ORDERS_VIEW_ORDER
  }, [ order.status ])

  const orderUrl = useMemo(() => {
    return generatePath(orderRoute, { brandSlug, orderId: order.id })
  }, [ orderRoute, brandSlug, order.id ])

  // Обработка клика по карточке
  const handleCardClick = useCallback(() => {
    history.push(orderUrl)
  }, [ history, orderUrl ])

  // Форматируем время слота
  const slotTime = useMemo(() => {
    if (!order.deliverySlot) return null
    return order.deliverySlot.slotTime
  }, [ order.deliverySlot ])

  // Цвет подсветки срочности
  const urgencyColor = useMemo(() => getUrgencyColor(order), [ order ])

  // Стиль карточки с подсветкой
  const cardStyle = useMemo(() => {
    if (!urgencyColor) return undefined
    return {
      backgroundColor: urgencyColor === '#f5222d' ? 'rgba(245, 34, 45, 0.08)' : 'rgba(250, 173, 20, 0.08)',
      borderLeft: `3px solid ${urgencyColor}`
    }
  }, [ urgencyColor ])

  return (
    <div className="OrderCard" onClick={handleCardClick} style={cardStyle}>
      {/* Строка 1: номер заказа + время */}
      <div className="OrderCard__Row">
        <Link
          to={orderUrl}
          className="OrderCard__Id"
          onClick={(e) => e.stopPropagation()}
        >
          #{order.id}
        </Link>
        {slotTime && (
          <span className="OrderCard__Time">{slotTime}</span>
        )}
      </div>

      {/* Строка 2: адрес */}
      {order.address && (
        <div className="OrderCard__Row">
          <span className="OrderCard__Address">{order.address}</span>
        </div>
      )}

      {/* Строка 3: сборщик */}
      <div className="OrderCard__Row">
        <span className="OrderCard__Staff">
          <PersonOutline width="14px" height="14px" color="#999" />
          {order.assembler
            ? `${order.assembler.first_name} ${order.assembler.last_name}`
            : 'Не назначен'}
        </span>
      </div>

      {/* Строка 3: курьер (если есть) */}
      {order.courier && (
        <div className="OrderCard__Row">
          <span className="OrderCard__Staff OrderCard__Staff--courier">
            <CarOutline width="14px" height="14px" color="#999" />
            {order.courier.first_name} {order.courier.last_name}
          </span>
        </div>
      )}
    </div>
  )
}

export default React.memo(OrderCard)
